import * as React from "react";
import classnames from "classnames";

import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { RecordDetails } from "./landing_types";
import { formatAmount, formatRecordDate } from "./landing_utils";
import Pagination from "./Pagination";
import RecordStage from "./RecordStage";

import css from "./ExpandableRecordList.module.scss";

import BANK from "../../../assets/images/icons/bank.svg";
import DOWN from "../../../assets/images/icons/down.svg";
import TIME_ICON from "../../../assets/images/icons/field_time.svg";
import TAG_ICON from "../../../assets/images/icons/tag.svg";

function RecordDescription({ description }: { description: string }): JSX.Element {
  const initialDescription = React.useMemo(() => {
    return description.length > 225 ? description.slice(0, 255) + "..." : description;
  }, [description]);

  const [expanded, setExpanded] = React.useState(false);

  return (
    <p className={css.recordDescription}>
      {expanded ? description : initialDescription}{" "}
      {description.length > 225 && (
        <button
          onClick={(e) => {
            setExpanded((ex) => !ex);
            e.stopPropagation();
          }}
        >
          {expanded ? "Read less" : "Read more"}{" "}
          <img
            height={12}
            width={12}
            src={DOWN}
            alt="down-icon"
            className={classnames({ [css.flipped]: expanded })}
          />
        </button>
      )}
    </p>
  );
}

type Props = {
  records: RecordDetails[];
  hideBuyer?: boolean;
  extraParamsForPaging?: Record<string, string>;
  currentPage: number;
  totalResultsCount: number;
  promoSection?: JSX.Element;
};

const DEFAULT_PAGE_SIZE = 12;

function ExpandableRecordList({
  records,
  hideBuyer,
  currentPage,
  totalResultsCount,
  extraParamsForPaging,
  promoSection,
}: Props): JSX.Element {
  return (
    <div>
      <ul className={css.expandableRecordList}>
        {!records.length ? (
          <div>No data to display</div>
        ) : (
          records.map((r, index) => {
            return (
              <React.Fragment key={r.guid}>
                {index === 3 && promoSection}
                <li
                  className={css.recordListItem}
                  onClick={() => (window.location.pathname = r.href)}
                >
                  <div className={css.itemHeader}>
                    <RecordStage record={r} className={css.stage} hideAssociatedDate />
                    {r.publish_date && (
                      <p className={css.publishDate}>
                        Published {toHumanRelativeDateFromNow(r.publish_date)}
                      </p>
                    )}
                  </div>
                  <h3 className={css.itemTitle}>
                    <a href={r.href} target="_blank" data-id="explore_articles" rel="noreferrer">
                      {r.name}
                    </a>
                  </h3>
                  <div className={css.detailsWrapper}>
                    {r.buyer && !hideBuyer && (
                      <span className={css.recordDetail}>
                        <img height={14} width={14} src={BANK} alt="bank-icon" />
                        {r.buyer.name}
                      </span>
                    )}
                    {r.value_src && (
                      <span className={css.recordDetail}>
                        <img height={14} width={14} src={TAG_ICON} alt="price-tag-icon" />

                        {formatAmount(r.value_src, r.currency)}
                      </span>
                    )}
                    {r.close_date && (
                      <span className={css.recordDetail}>
                        <img height={14} width={14} src={TIME_ICON} alt="time-icon" />
                        Close date: {formatRecordDate(r.close_date)}
                      </span>
                    )}
                    {r.expiry_date && (
                      <span className={css.recordDetail}>
                        <img height={14} width={14} src={TIME_ICON} alt="time-icon" />
                        Expiry date: {formatRecordDate(r.expiry_date)}
                      </span>
                    )}
                  </div>
                  {r.description && <RecordDescription description={r.description.trim()} />}
                </li>
              </React.Fragment>
            );
          })
        )}
      </ul>
      <Pagination
        currentPage={currentPage as number}
        totalPages={Math.ceil((totalResultsCount as number) / DEFAULT_PAGE_SIZE)}
        extraParams={extraParamsForPaging}
      />
    </div>
  );
}

export default ExpandableRecordList;
